import React from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router";
import { NavLink } from "react-router-dom";
import classnames from "classnames";
import { useQuery } from "@apollo/client";
import { getMenuByProfile, menuLabels } from "./menuItems";
import { GET_CONTRACTOR_RESERVATION_VISIBILITY } from "./queries";
import {
  Toolbar,
  Hidden,
  IconButton,
  Box,
  Grid,
  Collapse,
} from "@material-ui/core";
import { Menu as MenuIcon, Cancel as CancelIcon } from "@material-ui/icons";

import tokenProvider from "../../core/tokenProvider";
import Today from "./Today";
import MenuTouch from "./MenuTouch";
import Logo from "./Logo";
import OfficeSelect from "./OfficeSelect";
import LogOutUser from "helpers/LogOutUser";
import LogoutLink from "./LogoutLink";
import ConnectedUser from "./ConnectedUser";
import HistoryButton from "./HistoryButton";
import Config from "config";
import useOffice from "components/Offices/useOffice";
import {
  AppBarWrapper,
  AppBar,
  NavbarGrid,
  MainNavButton,
  SettingsNav,
  SettingsNavItemWithSubmenu,
  StatusBar,
  LinkButton,
  LinkButtonText,
  SubmenuDropdown,
  SubmenuItem,
} from "./styled";
import useAppState from "../../helpers/useAppState";
import usePermissions from "helpers/usePermissions";
import { DriverAppNavbarLink } from "components/shared/DriverAppBanner";
import useDriverAppAnnouncement from "components/shared/DriverAppBanner/useDriverAppAnnouncement";
import { useTheme } from "@material-ui/core/styles";

const VersionNumber = () => {
  const version = Config.get("version");
  const theme = useTheme();
  const color =
    theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.25)" : "#20274a3b";
  return (
    <p style={{ marginLeft: "auto", paddingRight: "16px", color }}>
      v{version}
    </p>
  );
};

const Navbar = ({ location, logoLink, logoAlt = "" }) => {
  const { pathname } = location;

  const [isOpenMenuTouch, setIsOpenMenuTouch] = React.useState(false);
  const [isOpenSettingsNav, setIsOpenSettingsNav] = React.useState(
    pathname.includes("/settings/")
  );

  const { profile: userProfile, id: userId } = useAppState().user;
  const { isSubcontractor, canAccessBilling } = usePermissions();
  const { isVisible: isAnnouncementVisible } = useDriverAppAnnouncement();
  const showAnnouncement = isAnnouncementVisible && pathname !== "/";
  const hasToken = tokenProvider.getToken();
  const [currentOffice] = useOffice();
  const [openSubmenu, setOpenSubmenu] = React.useState(null);
  const closeTimerRef = React.useRef(null);
  const anchorRef = React.useRef(null);
  const { data: contractorReservationVisibilityData } = useQuery(
    GET_CONTRACTOR_RESERVATION_VISIBILITY,
    {
      variables: { id: userId },
      skip: userProfile !== "contractor",
    }
  );

  const contractorCanSeeReservations =
    userProfile !== "contractor" ||
    !!contractorReservationVisibilityData?.user?.contractors?.some(
      ({ active, is_reservation_enabled: isReservationEnabled }) =>
        active && Boolean(isReservationEnabled)
    );
  const menuItems = React.useMemo(() => {
    const items = getMenuByProfile(userProfile, canAccessBilling);

    if (contractorCanSeeReservations) {
      return items;
    }

    return {
      ...items,
      mainNav: items.mainNav.filter(({ href }) => href !== "/reservations"),
    };
  }, [contractorCanSeeReservations, userProfile, canAccessBilling]);

  const handleSubmenuEnter = (key, el) => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    anchorRef.current = el;
    setOpenSubmenu(key);
  };

  const handleSubmenuLeave = () => {
    closeTimerRef.current = setTimeout(() => {
      setOpenSubmenu(null);
      anchorRef.current = null;
    }, 200);
  };

  const closeSettingsNav = () => {
    setIsOpenSettingsNav(false);
  };

  const isActiveMainNavButton = (route) =>
    pathname === route && !isOpenSettingsNav;

  const handleSettingsOnclick = (href) =>
    href === "/settings" && setIsOpenSettingsNav(true);
  return (
    hasToken && (
      <AppBarWrapper>
        <AppBar position="fixed">
          <Box px={2}>
            <Toolbar disableGutters>
              <NavbarGrid container>
                <Hidden mdDown>
                  <Grid item lg={4} className="main-nav">
                    {menuItems.mainNav.map(
                      ({ title, href, icon, isNavLink }) => {
                        const mainNavButton = (
                          <MainNavButton
                            key={title}
                            title={title.toUpperCase()}
                            className={classnames({
                              active: isNavLink
                                ? isActiveMainNavButton(href)
                                : isOpenSettingsNav,
                            })}
                            onClick={() => handleSettingsOnclick(href)}
                          >
                            {icon}
                          </MainNavButton>
                        );

                        return isNavLink ? (
                          <NavLink
                            to={href}
                            key={`${title}-link`}
                            onClick={closeSettingsNav}
                          >
                            {mainNavButton}
                          </NavLink>
                        ) : (
                          mainNavButton
                        );
                      }
                    )}
                  </Grid>
                </Hidden>

                <Grid item xs={10} lg={4} className="logo">
                  <Logo src={logoLink} alt={logoAlt} />
                </Grid>

                <Hidden mdDown>
                  <Grid item lg={4} className="info">
                    <Grid
                      container
                      justifyContent="flex-end"
                      alignItems="center"
                    >
                      <Grid item>
                        <HistoryButton />
                      </Grid>
                      <Grid item>
                        <ConnectedUser />
                      </Grid>
                      <Grid item style={{ marginLeft: 8, marginRight: 16 }}>
                        <LogOutUser>
                          {(logout) => (
                            <LogoutLink
                              button
                              logout={logout}
                              loginPage={"/login"}
                            >
                              <CancelIcon />
                              <LinkButtonText>
                                {menuLabels.logout.toUpperCase()}
                              </LinkButtonText>
                            </LogoutLink>
                          )}
                        </LogOutUser>
                      </Grid>
                      <Grid item>
                        <Today className="info__date" />
                      </Grid>
                    </Grid>
                    {!isSubcontractor && (
                      <OfficeSelect htmlId="navbar-office-selector" />
                    )}
                  </Grid>
                </Hidden>

                <Hidden lgUp>
                  <Grid item xs={2} className="menu-icon">
                    <IconButton
                      color="inherit"
                      aria-label="open drawer"
                      edge="end"
                      onClick={() => {
                        setIsOpenMenuTouch(true);
                      }}
                    >
                      <MenuIcon />
                    </IconButton>
                  </Grid>
                </Hidden>
              </NavbarGrid>
            </Toolbar>
          </Box>
          <Hidden mdDown>
            <Collapse in={isOpenSettingsNav}>
              <SettingsNav>
                {menuItems.settingsLinks.map(({ text, href, officeTabs }) => {
                  if (officeTabs && currentOffice?.id) {
                    return (
                      <SettingsNavItemWithSubmenu
                        key={text}
                        onMouseEnter={(e) =>
                          handleSubmenuEnter(text, e.currentTarget)
                        }
                        onMouseLeave={handleSubmenuLeave}
                      >
                        <LinkButton to={href} style={{ margin: 0 }}>
                          {text.toUpperCase()}
                        </LinkButton>
                        <SubmenuDropdown
                          className={openSubmenu === text ? "open" : ""}
                        >
                          {officeTabs.map(({ label, index }) => (
                            <SubmenuItem
                              key={index}
                              to={`/settings/offices/edit/${currentOffice.id}?tab=${index}`}
                              onClick={() => setOpenSubmenu(null)}
                            >
                              {label}
                            </SubmenuItem>
                          ))}
                        </SubmenuDropdown>
                      </SettingsNavItemWithSubmenu>
                    );
                  }
                  return (
                    <LinkButton key={text} to={href}>
                      {text.toUpperCase()}
                    </LinkButton>
                  );
                })}
                <VersionNumber />
              </SettingsNav>
            </Collapse>
          </Hidden>
          {(menuItems.status.length > 0 || showAnnouncement) && (
            <Hidden mdDown>
              <StatusBar>
                {showAnnouncement && <DriverAppNavbarLink />}
                {menuItems.status.map(({ text, href, icon }) => (
                  <LinkButton key={text} to={href}>
                    {icon}
                    <LinkButtonText>{text.toUpperCase()}</LinkButtonText>
                  </LinkButton>
                ))}
              </StatusBar>
            </Hidden>
          )}
        </AppBar>
        <MenuTouch
          menuItems={menuItems}
          open={isOpenMenuTouch}
          handleDrawerClose={() => {
            setIsOpenMenuTouch(false);
          }}
        />
      </AppBarWrapper>
    )
  );
};

Navbar.propTypes = {
  location: PropTypes.object.isRequired,
  logoLink: PropTypes.string.isRequired,
  logoAlt: PropTypes.string,
};

export default withRouter(Navbar);
